import THREE from "three";
import i18n from "i18next";
import { GlobalManagerProvider } from '../../application/GlobalManagerMixin';
import { VIEW_TYPES } from "../../application/PreferenceNames";

// Number.MIN_VALUE is specified as the smallest number greater than 0 possible (5e-324). In Fusion on Mac,
// Number.MIN_VALUE is exactly 0 for reasons that escape me. This causes issues with the Autocam code. Interestingly,
// Fusion on Mac can't represent the number 5e-324. The smallest it can do is 3e-308 (found via trial and error).
// Sadly, this constant can't be overwritten so monkey patching is out of the question. Instead use our own constant.
// See DC-11061
const MIN_VALUE = Number.MIN_VALUE || 3e-308;

const EPSILON = 0.00001;

/**
 * Clamps a vector to an axis aligned unit vector if it's sufficiently close. This is to help deal with THREE doing:
 * var t = new THREE.Vector3(0, 0, -0.6873695734180347);
 * t.normalize();
 * t.z; // -0.9999999999999999
 *
 * and us doing direct comparisons of floating point numbers.
 *
 * @param {THREE.Vector3} vec - The vector to check and clamp. This vector may be modified
 */
function clampToUnitAxisIfNeeded(vec) {
  const tolerance = 1e-12;
  if (Math.abs(vec.x) < tolerance && Math.abs(vec.y) < tolerance) {
    vec.set(0, 0, vec.z > 0 ? 1 : -1);
  } else if (Math.abs(vec.y) < tolerance && Math.abs(vec.z) < tolerance) {
    vec.set(vec.x > 0 ? 1 : -1, 0, 0);
  } else if (Math.abs(vec.z) < tolerance && Math.abs(vec.x) < tolerance) {
    vec.set(0, vec.y > 0 ? 1 : -1, 0);
  }
}

/**
 * Autocam is the container for the view cube and steering wheel classes.
 * It contains math for camera transformations and most of the functions are retrieved from SampleCAM.
 * Refer to their documentation for explanation.
 */
export class Autocam extends GlobalManagerProvider {
  // Simple class fields for properties that don't need constructor logic
  cube = null;
  renderer = 'WEBGL';
  startState = {};
  orthographicFaces = false;

  _dropDownMenu = null;
  _changing = false;

  //delta Time
  _startTime = Date.now();
  _deltaTime = undefined;
  _setHomeDeferred = false;

  recordTime = true;

  //Control variables
  ortho = false;

  _prevEye = undefined;
  _prevCenter = undefined;
  _prevUp = undefined;
  _prevPivot = undefined;

  _rotInitial = new THREE.Quaternion();
  _rotFinal = new THREE.Quaternion();
  _rotTwist = new THREE.Quaternion();
  _rotSpin = new THREE.Quaternion();
  _distInitial = undefined;
  _distFinal = undefined;

  /**
  * Holds the default look speed multiplier of 2.0
  * @type {number}
  */
  userLookSpeed = 2.0;

  /**
  * Holds the orbit multiplier of 5.0
  * @type {number}
  */
  orbitMultiplier = 5.0;
  currentlyAnimating = false;


  //orbit
  preserveOrbitUpDirection = true;
  alignOrbitUpDirection = true;
  constrainOrbitHorizontal = false;
  constrainOrbitVertical = false;
  doCustomOrbit = false;
  snapOrbitDeadZone = 0.045;
  snapOrbitThresholdH = this.snapOrbitThresholdV = THREE.Math.degToRad(15.0);
  snapOrbitAccelerationAX = this.snapOrbitAccelerationAY = 1.5;
  snapOrbitAccelerationBX = this.snapOrbitAccelerationBY = 2.0;
  snapOrbitAccelerationPointX = this.snapOrbitAccelerationPointY = 0.5;

  combined = false;

  //variables used for snapping
  useSnap = false;
  lockDeltaX = 0.0;
  lockedX = false;
  lastSnapRotateX = 0.0;
  lockDeltaY = 0.0;
  lockedY = false;
  lastSnapRotateY = 0.0;
  lastSnapDir = new THREE.Vector3(0, 0, 0);

  //shot
  _shotParams = { destinationPercent: 1.0, duration: 1.0, zoomToFitScene: true, useOffAxis: false };
  shotParams = this._shotParams; // Expose these for modification
  _camParamsInitial = undefined;
  _camParamsFinal = undefined;

  viewCubeMenuOpen = false;
  menuSize = new THREE.Vector2(0, 0);
  menuOrigin = new THREE.Vector2(0, 0);

  // Callback functions
  cameraChangedCallback = function () {};
  pivotDisplayCallback = function () {};
  transitionCompletedCallback = function () {};

  constructor(camera, navApi, canvas) {
    super();

    this.camera = camera;
    this.navApi = navApi; // TODO: use this for camera sync.
    this.canvas = canvas;

    this.center = this.camera.target ? this.camera.target.clone() : new THREE.Vector3(0, 0, 0);
    this.pivot = this.camera.pivot ? this.camera.pivot.clone() : this.center.clone();

    this.sceneUpDirection = this.camera.worldup ? this.camera.worldup.clone() : this.camera.up.clone();
    this.sceneFrontDirection = this.getWorldFrontVector();

    //dir, up, left vector
    this.dir = this.getView();

    // Compute "real" camera up:
    this.setCameraUp(this.camera.up);

    this.saveCenter = this.center.clone();
    this.savePivot = this.pivot.clone();
    this.saveEye = this.camera.position.clone();
    this.saveUp = this.camera.up.clone();
    this.cubeFront = this.sceneFrontDirection.clone().cross(this.sceneUpDirection).normalize();

    this.setHomeViewFrom(this.camera);

    //look
    camera.keepSceneUpright = true;
    camera.lookAt(this.center);


    const animate = () => {
      if (this.recordTime) {
        requestAnimationFrame(animate);
      }
      // Is there an assumption here about the order of animation frame callbacks?
      var now = Date.now();
      this._deltaTime = now - this._startTime;
      this._startTime = now;
    };
    animate();

    // function windowResize(){
    // refresh camera on size change

    // We handle this elsewhere
    /*
    renderer.setSize( window.innerWidth, window.innerHeight );
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.topFov = camera.bottomFov = camera.fov/2;
    camera.leftFov = camera.rightFov = (camera.aspect * camera.fov)/2;
    camera.updateProjectionMatrix();
    */
    // }

    /***
    windowResize();
    window.addEventListener('resize', windowResize, false);
    ***/
  }

  _changed(worldUpChanged) {
    this._changing = true;
    this.camera.target.copy(this.center);
    this.camera.pivot.copy(this.pivot);

    if (this.camera.worldup)
    this.camera.worldup.copy(this.sceneUpDirection);else

    this.camera.up.copy(this.sceneUpDirection);

    this.cameraChangedCallback(worldUpChanged);
    this._changing = false;
  }

  dtor() {
    this.cube = null;
    this.cameraChangedCallback = null;
    this.pivotDisplayCallback = null;
    this.transitionCompletedCallback = null;
    this.canvas = null;
    this.recordTime = false;
    this.afAnimateTransition && cancelAnimationFrame(this.afAnimateTransition);
  }

  registerCallbackCameraChanged(callback) {
    this.cameraChangedCallback = callback;
  }

  registerCallbackPivotDisplay(callback) {
    this.pivotDisplayCallback = callback;
  }

  registerCallbackTransitionCompleted(callback) {
    this.transitionCompletedCallback = callback;
  }

  showPivot(state)
  {
    this.pivotDisplayCallback(state);
  }

  setWorldUpVector(newUp)
  {
    if (this._changing)
    return;

    if (newUp && newUp.lengthSq() > 0 && !newUp.normalize().equals(this.sceneUpDirection))
    {
      // Changing up resets the front face:
      this.sceneUpDirection.copy(newUp);
      this.sceneFrontDirection.copy(this.getWorldFrontVector());
      this.cubeFront.copy(this.sceneFrontDirection).cross(this.sceneUpDirection).normalize();
      if (this.cube)
      requestAnimationFrame(this.cube.render);
    }
  }

  getWorldUpVector()
  {
    return this.sceneUpDirection.clone();
  }

  // Assumes sceneUpDirection is set.
  getWorldRightVector()
  {
    var vec = this.sceneUpDirection.clone();

    if (Math.abs(vec.z) <= Math.abs(vec.y))
    {
      // Cross(Vertical, ZAxis)
      vec.set(vec.y, -vec.x, 0);
    } else
    if (vec.z >= 0)
    {
      // Cross(YAxis, Vertical)
      vec.set(vec.z, 0, -vec.x);
    } else

    {
      // Cross(Vertical, YAxis)
      vec.set(-vec.z, 0, vec.x);
    }
    return vec.normalize();
  }

  // Assumes sceneUpDirection is set.
  getWorldFrontVector()
  {
    var up = this.getWorldUpVector();
    return up.cross(this.getWorldRightVector()).normalize();
  }

  goToView(viewVector) {
    if (this.navApi.isActionEnabled('gotoview')) {
      var destination = {
        position: viewVector.position.clone(),
        up: viewVector.up.clone(),
        center: viewVector.center.clone(),
        pivot: viewVector.pivot.clone(),
        fov: viewVector.fov,
        worldUp: viewVector.worldUp.clone(),
        isOrtho: viewVector.isOrtho
      };

      // add global position/center/pivot - which are stable under globalOffset changes
      this.addGlobalPositions(destination);

      this.elapsedTime = 0;
      this.animateTransition(destination);
    }
  }

  getCurrentView() {
    return {
      position: this.camera.position.clone(),
      up: this.camera.up.clone(),
      center: this.center.clone(),
      pivot: this.pivot.clone(),
      fov: this.camera.fov,
      worldUp: this.sceneUpDirection.clone(),
      isOrtho: this.camera.isPerspective === false
    };
  }

  setCurrentViewAsHome(focusFirst) {
    if (focusFirst) {
      this.navApi.setRequestFitToView(true);
      this._setHomeDeferred = true;
    } else
    {
      this.homeVector = this.getCurrentView();
    }
  }

  // This method sets both the "current" home and the "original" home.
  // The latter is used for the "reset home" function.
  setHomeViewFrom(camera) {
    var pivot = camera.pivot ? camera.pivot : this.center;
    var center = camera.target ? camera.target : this.pivot;
    var worldup = camera.worldup ? camera.worldup : this.sceneUpDirection;

    this.homeVector = {
      position: camera.position.clone(),
      up: camera.up.clone(),
      center: center.clone(),
      pivot: pivot.clone(),
      fov: camera.fov,
      worldUp: worldup.clone(),
      isOrtho: camera.isPerspective === false
    };

    this.originalHomeVector = {
      position: camera.position.clone(),
      up: camera.up.clone(),
      center: center.clone(),
      pivot: pivot.clone(),
      fov: camera.fov,
      worldUp: worldup.clone(),
      worldFront: this.sceneFrontDirection.clone(), // Extra for reset orientation
      isOrtho: camera.isPerspective === false
    };

    // Remember global positions as well - which keep stable under globalOffset changes.
    this.addGlobalPositions(this.homeVector);
    this.addGlobalPositions(this.originalHomeVector);
  }

  toPerspective() {
    if (!this.camera.isPerspective) {
      this.camera.toPerspective();
      this._changed(false);
    }
  }

  toOrthographic() {
    if (this.camera.isPerspective) {
      this.camera.toOrthographic();
      this._changed(false);
    }
  }

  setOrthographicFaces(state) {
    this.orthographicFaces = state;
  }

  getViewType() {
    if (this.orthographicFaces) {
      return VIEW_TYPES.PERSPECTIVE_ORTHO_FACES;
    }

    return this.camera.isPerspective ? VIEW_TYPES.PERSPECTIVE : VIEW_TYPES.ORTHOGRAPHIC;
  }

  setViewType(viewType, isFaceView) {
    switch (viewType) {
      case VIEW_TYPES.ORTHOGRAPHIC:
        this.setOrthographicFaces(false);
        this.toOrthographic();
        return true;
      case VIEW_TYPES.PERSPECTIVE:
        this.setOrthographicFaces(false);
        this.toPerspective();
        return true;
      case VIEW_TYPES.PERSPECTIVE_ORTHO_FACES:
        this.setOrthographicFaces(true);
        if (isFaceView === undefined) {
          isFaceView = this.isFaceView();
        }

        if (isFaceView) {
          this.toOrthographic();
        } else {
          this.toPerspective();
        }
        return true;
      default:
        // This viewType is not supported.
        // We do not want to call the view type changed callback
        return false;
    }
  }

  goHome() {
    if (this.navApi.isActionEnabled('gotoview')) {
      this.navApi.setPivotSetFlag(false);
      this.goToView(this.homeVector);
    }
  }

  resetHome() {
    this.homeVector.position.copy(this.originalHomeVector.position);
    this.homeVector.up.copy(this.originalHomeVector.up);
    this.homeVector.center.copy(this.originalHomeVector.center);
    this.homeVector.pivot.copy(this.originalHomeVector.pivot);
    this.homeVector.fov = this.originalHomeVector.fov;
    this.homeVector.worldUp.copy(this.originalHomeVector.worldUp);
    this.homeVector.isOrtho = this.originalHomeVector.isOrtho;
    this.goHome();
  }

  getView() {
    return this.center.clone().sub(this.camera.position);
  }

  setCameraUp(up) {
    var view = this.dir.clone();
    var right = view.cross(up).normalize();
    if (right.lengthSq() === 0)
    {
      // Try again after perturbing eye direction:
      view.copy(this.dir);
      if (up.z > up.y)
      view.y += 0.0001;else

      view.z += 0.0001;

      right = view.cross(up).normalize();
    }
    // Orthogonal camera up direction:
    this.camera.up.copy(right).cross(this.dir).normalize();
  }

  // Add global positions to view. These are used on globalOffset changes.
  addGlobalPositions(view) {
    // use globalOffset of current camera by default
    var globalOffset = this.camera.globalOffset;

    view.globalPosition = view.position.clone().add(globalOffset);
    view.globalCenter = view.center.clone().add(globalOffset);
    view.globalPivot = view.pivot.clone().add(globalOffset);
  }

  _updateViewGlobalOffset(view, newOffset) {
    view.position.copy(view.globalPosition).sub(newOffset);
    view.center.copy(view.globalCenter).sub(newOffset);
    view.pivot.copy(view.globalPivot).sub(newOffset);
  }

  // If the dynamic viewer globalOffset changes, we must update our saved views.
  onGlobalOffsetChanged(newOffset) {
    this._updateViewGlobalOffset(this.homeVector, newOffset);
    this._updateViewGlobalOffset(this.originalHomeVector, newOffset);
    this.camera.setGlobalOffset(newOffset);
  }

  setCube(viewcube)
  {
    this.cube = viewcube; // DOH!!!
  }

  // Sync our local data from the given external camera:
  sync(clientCamera) {
    if (clientCamera.isPerspective !== this.camera.isPerspective) {
      if (clientCamera.isPerspective) {
        this.camera.toPerspective();
      } else
      {
        this.camera.toOrthographic();
        if (clientCamera.saveFov)
        this.camera.saveFov = clientCamera.saveFov;
      }
    }
    this.camera.fov = clientCamera.fov;
    this.camera.position.copy(clientCamera.position);

    if (clientCamera.target) {
      this.center.copy(clientCamera.target);
      this.camera.target.copy(clientCamera.target);
    }
    if (clientCamera.pivot) {
      this.pivot.copy(clientCamera.pivot);
      this.camera.pivot.copy(clientCamera.pivot);
    }
    this.dir.copy(this.center).sub(this.camera.position);

    this.setCameraUp(clientCamera.up);

    var worldUp = clientCamera.worldup ? clientCamera.worldup : clientCamera.up;
    if (worldUp.distanceToSquared(this.sceneUpDirection) > 0.0001) {
      this.setWorldUpVector(worldUp);
    }

    if (this._setHomeDeferred && !this.navApi.getTransitionActive()) {
      this._setHomeDeferred = false;
      this.setCurrentViewAsHome(false);
    }
    if (this.cube)
    requestAnimationFrame(this.cube.render);
  }

  refresh() {
    if (this.cube)
    this.cube.refreshCube();
  }

  _linearClamp(x, a, b) {
    if (x <= a) {return 0.0;}
    if (x >= b) {return 1.0;}

    return (x - a) / (b - a);
  }

  _easeClamp(x, a, b) {
    if (x <= a) {return 0.0;}
    if (x >= b) {return 1.0;}

    var t = (x - a) / (b - a);
    return 0.5 * (Math.sin((t - 0.5) * Math.PI) + 1.0);
  }

  _linearInterp(t, a, b) {
    return a * (1.0 - t) + b * t;
  }

  _equalityClamp(x, a, b) {
    if (x <= a) {return a;}
    if (x >= b) {return b;}

    return x;
  }

  _round2(x) {
    return Math.round(x * 100) / 100;
  }

  _round1(x) {
    return Math.round(x * 10) / 10;
  }

  /*      SHOT OPERATION      */
  //transitions smoothly to destination
  animateTransition(destination) {

    // GlobalOffset may change during a transition. So, we make sure here that
    // the viewerCoords are computed based on latest globalOffset.
    this._updateViewGlobalOffset(destination, this.camera.globalOffset);

    if (!destination) {return;}

    var worldUpChanged = false;
    var unitTime = 0.0;

    this.setCameraOrtho(destination.isOrtho);

    if (this.elapsedTime >= this._shotParams.duration) {
      unitTime = 1.0;

      this.center.copy(destination.center);
      this.pivot.copy(destination.pivot);
      this.camera.position.copy(destination.position);
      this.camera.up.copy(destination.up);
      this.camera.target.copy(destination.center);
      if (!destination.isOrtho)
      this.camera.fov = destination.fov;
      this.camera.dirty = true;

      worldUpChanged = !destination.worldUp.equals(this.sceneUpDirection);
      if (worldUpChanged)
      this.setWorldUpVector(destination.worldUp);

      this.currentlyAnimating = false;
      this._changed(worldUpChanged);
      this.showPivot(false);
      if (this.cube)
      requestAnimationFrame(this.cube.render);

      //this.addHistoryElement();
      this.navApi.setTransitionActive(false);
      this.transitionCompletedCallback();
      return;
    }
    this.currentlyAnimating = true;
    this.showPivot(true);
    this.navApi.setTransitionActive(true);

    var tMax = this._shotParams.destinationPercent;
    unitTime = this._easeClamp(this.elapsedTime / this._shotParams.duration, 0.0, tMax);
    var oneMinusTime = 1.0 - unitTime;
    this.elapsedTime += this._deltaTime / 500;

    var center = this.center.clone().multiplyScalar(oneMinusTime).add(destination.center.clone().multiplyScalar(unitTime));
    var position = this.camera.position.clone().multiplyScalar(oneMinusTime).add(destination.position.clone().multiplyScalar(unitTime));
    var up = this.camera.up.clone().multiplyScalar(oneMinusTime).add(destination.up.clone().multiplyScalar(unitTime));
    var pivot = this.camera.pivot.clone().multiplyScalar(oneMinusTime).add(destination.pivot.clone().multiplyScalar(unitTime));
    var worldUp = this.sceneUpDirection.clone().multiplyScalar(oneMinusTime).add(destination.worldUp.clone().multiplyScalar(unitTime));
    var fov = this.camera.fov * oneMinusTime + destination.fov * unitTime;

    this.center.copy(center);
    this.pivot.copy(pivot);
    this.camera.position.copy(position);
    this.camera.up.copy(up);
    this.camera.target.copy(center);
    if (!destination.isOrtho)
    this.camera.fov = fov;
    this.camera.dirty = true;

    worldUpChanged = worldUp.distanceToSquared(this.sceneUpDirection) > 0.0001;
    if (worldUpChanged)
    this.setWorldUpVector(worldUp);

    this.camera.lookAt(this.center);
    this._changed(worldUpChanged);

    if (this.cube)
    requestAnimationFrame(this.cube.render);

    this.afAnimateTransition = requestAnimationFrame(() => {this.animateTransition(destination);});
  }

  //used for view cube transforms, to see difference between this and linear interpolation watch
  //http://www.youtube.com/watch?v=uNHIPVOnt-Y
  sphericallyInterpolateTransition(completionCallback)
  {
    var center, position, up;
    var unitTime = 0.0;
    this.currentlyAnimating = true;
    this.navApi.setTransitionActive(true);

    if (this.elapsedTime >= this._shotParams.duration) {
      unitTime = 1.0;
      this.currentlyAnimating = false;
    } else
    {
      var tMax = this._shotParams.destinationPercent;
      unitTime = this._easeClamp(this.elapsedTime / this._shotParams.duration, 0.0, tMax);
      this.elapsedTime += this._deltaTime / 500;
    }

    // This seems to avoid some error in the rotation:
    if (unitTime === 1.0) {
      position = this._camParamsFinal.position;
      center = this._camParamsFinal.center;
      up = this._camParamsFinal.up;
    } else
    {
      var M = new THREE.Matrix3();
      var rot = this._rotInitial.clone();
      rot.slerp(this._rotFinal, unitTime);
      M.makeRotationFromQuaternion(rot);
      var dist = this._linearInterp(unitTime, this._distInitial, this._distFinal);

      var e = M.elements;

      center = this._camParamsInitial.center.clone().multiplyScalar(1.0 - unitTime).add(this._camParamsFinal.center.clone().multiplyScalar(unitTime));
      position = center.clone().sub(new THREE.Vector3(e[0], e[1], e[2]).multiplyScalar(dist));
      up = new THREE.Vector3(e[3], e[4], e[5]);
    }
    this.center.copy(center);
    this.camera.position.copy(position);
    this.camera.up.copy(up);

    // The above code will have to change if we want the proper rotation
    // to occur about the pivot point instead of the center.
    if (!this.navApi.getUsePivotAlways())
    this.pivot.copy(center);

    this.camera.lookAt(this.center);

    if (this.currentlyAnimating === true) {
      this.showPivot(true);
      requestAnimationFrame(() => {this.sphericallyInterpolateTransition(completionCallback);});
    } else
    {
      this.navApi.setTransitionActive(false);
      this.showPivot(false);
      //this.addHistoryElement();

      if (this.orthographicFaces && this.isFaceView())
      this.setCameraOrtho(true);

      if (completionCallback)
      completionCallback();
    }
    this._changed(false);
    if (this.cube)
    requestAnimationFrame(this.cube.render);
  }

  //This is used to determine the relation between camera up vector and scene direction, used to determine which
  //face to translate to when clicking on a viewcube arrow
  getOrientation() {
    if (!this.cube)
    return;

    var camX = this._round1(this.camera.up.x);
    var camY = this._round1(this.camera.up.y);
    var camZ = this._round1(this.camera.up.z);
    var sceneFront = this.sceneFrontDirection.clone();
    var sceneUp = this.sceneUpDirection.clone();
    var sceneRight = this.sceneFrontDirection.clone().cross(this.sceneUpDirection).normalize();
    sceneFront.x = this._round1(sceneFront.x);
    sceneFront.y = this._round1(sceneFront.y);
    sceneFront.z = this._round1(sceneFront.z);
    sceneUp.x = this._round1(sceneUp.x);
    sceneUp.y = this._round1(sceneUp.y);
    sceneUp.z = this._round1(sceneUp.z);
    sceneRight.x = this._round1(sceneRight.x);
    sceneRight.y = this._round1(sceneRight.y);
    sceneRight.z = this._round1(sceneRight.z);
    var sceneLeft = sceneRight.clone().multiplyScalar(-1);
    var sceneDown = sceneUp.clone().multiplyScalar(-1);
    var sceneBack = sceneFront.clone().multiplyScalar(-1);

    switch (this.cube.currentFace) {
      case "front":
        if (sceneUp.x == camX && sceneUp.y == camY && sceneUp.z == camZ)
        return "up";else
        if (sceneDown.x == camX && sceneDown.y == camY && sceneDown.z == camZ)
        return "down";else
        if (sceneRight.x == camX && sceneRight.y == camY && sceneRight.z == camZ)
        return "right";else
        if (sceneLeft.x == camX && sceneLeft.y == camY && sceneLeft.z == camZ)
        return "left";
        break;
      case "right":
        if (sceneUp.x == camX && sceneUp.y == camY && sceneUp.z == camZ)
        return "up";else
        if (sceneDown.x == camX && sceneDown.y == camY && sceneDown.z == camZ)
        return "down";else
        if (sceneBack.x == camX && sceneBack.y == camY && sceneBack.z == camZ)
        return "left";else
        if (sceneFront.x == camX && sceneFront.y == camY && sceneFront.z == camZ)
        return "right";
        break;
      case "left":
        if (sceneUp.x == camX && sceneUp.y == camY && sceneUp.z == camZ)
        return "up";else
        if (sceneDown.x == camX && sceneDown.y == camY && sceneDown.z == camZ)
        return "down";else
        if (sceneFront.x == camX && sceneFront.y == camY && sceneFront.z == camZ)
        return "left";else
        if (sceneBack.x == camX && sceneBack.y == camY && sceneBack.z == camZ)
        return "right";
        break;
      case "back":
        if (sceneUp.x == camX && sceneUp.y == camY && sceneUp.z == camZ)
        return "up";else
        if (sceneDown.x == camX && sceneDown.y == camY && sceneDown.z == camZ)
        return "down";else
        if (sceneLeft.x == camX && sceneLeft.y == camY && sceneLeft.z == camZ)
        return "right";else
        if (sceneRight.x == camX && sceneRight.y == camY && sceneRight.z == camZ)
        return "left";
        break;
      case "top":
        if (sceneBack.x == camX && sceneBack.y == camY && sceneBack.z == camZ)
        return "down";else
        if (sceneFront.x == camX && sceneFront.y == camY && sceneFront.z == camZ)
        return "up";else
        if (sceneRight.x == camX && sceneRight.y == camY && sceneRight.z == camZ)
        return "right";else
        if (sceneLeft.x == camX && sceneLeft.y == camY && sceneLeft.z == camZ)
        return "left";
        break;
      case "bottom":
        if (sceneFront.x == camX && sceneFront.y == camY && sceneFront.z == camZ)
        return "down";else
        if (sceneBack.x == camX && sceneBack.y == camY && sceneBack.z == camZ)
        return "up";else
        if (sceneRight.x == camX && sceneRight.y == camY && sceneRight.z == camZ)
        return "right";else
        if (sceneLeft.x == camX && sceneLeft.y == camY && sceneLeft.z == camZ)
        return "left";
        break;
    }
  }

  setCameraOrtho(yes) {
    if (yes && this.camera.isPerspective)
    this.camera.toOrthographic();

    if (!yes && !this.camera.isPerspective)
    this.camera.toPerspective();
  }

  resetOrientation() {
    if (this.cube) {
      this.cube.showCompass(this.cube.prevRenderCompass);
    }

    this.setCameraOrtho(this.originalHomeVector.isOrtho);
    this.sceneUpDirection.copy(this.originalHomeVector.worldUp);
    this.sceneFrontDirection.copy(this.originalHomeVector.worldFront);
    this.cubeFront.copy(this.sceneFrontDirection).cross(this.sceneUpDirection).normalize();
    this.setCameraUp(this.sceneUpDirection);
    this._changed(true);
  }

  setCurrentViewAsFront() {
    if (this.cube) {
      this.cube.currentFace = "front";
      this.cube.showCompass(false); // hide the compass if the user changes the view
    }

    this.sceneUpDirection.copy(this.camera.up.clone());
    clampToUnitAxisIfNeeded(this.sceneUpDirection);

    this.sceneFrontDirection.copy(this.getView()).normalize();
    clampToUnitAxisIfNeeded(this.sceneFrontDirection);

    this.cubeFront.copy(this.sceneFrontDirection).cross(this.sceneUpDirection).normalize();
    clampToUnitAxisIfNeeded(this.cubeFront);

    if (this.orthographicFaces)
    this.setCameraOrtho(true);

    this._changed(true);
  }

  setCurrentViewAsTop() {
    if (this.cube) {
      this.cube.currentFace = "top";
      this.cube.showCompass(false); // hide the compass if the user changes the view
    }

    this.sceneUpDirection.copy(this.getView()).multiplyScalar(-1).normalize();
    clampToUnitAxisIfNeeded(this.sceneUpDirection);

    this.sceneFrontDirection.copy(this.camera.up);
    clampToUnitAxisIfNeeded(this.sceneFrontDirection);

    this.cubeFront.copy(this.sceneFrontDirection).cross(this.sceneUpDirection).normalize();
    clampToUnitAxisIfNeeded(this.cubeFront);

    this._changed(true);
  }

  calculateCubeTransform(faceString) {
    var worldUp = this.sceneUpDirection.clone();
    var worldFront = this.sceneFrontDirection.clone();
    var worldRight = this.sceneFrontDirection.clone().cross(this.sceneUpDirection).normalize();

    this._camParamsInitial = this.camera.clone();
    this._camParamsInitial.center = this.center.clone();
    this._camParamsInitial.pivot = this.pivot.clone();

    this._camParamsFinal = this.camera.clone();
    this._camParamsFinal.center = this.center.clone();
    this._camParamsFinal.pivot = this.pivot.clone();

    // find movement offset based on given boolean flags
    var offset = new THREE.Vector3(0, 0, 0);
    if (faceString.indexOf('back') >= 0) {
      offset = offset.add(worldFront);
    }
    if (faceString.indexOf('front') >= 0) {
      offset = offset.sub(worldFront);
    }
    if (faceString.indexOf('top') >= 0) {
      offset = offset.add(worldUp);
    }
    if (faceString.indexOf('bottom') >= 0) {
      offset = offset.sub(worldUp);
    }
    if (faceString.indexOf('right') >= 0) {
      offset = offset.add(worldRight);
    }
    if (faceString.indexOf('left') >= 0) {
      offset = offset.sub(worldRight);
    }
    var upDir = worldUp;

    // view looking at top or bottom chosen
    var test = offset.clone().normalize();

    if (1.0 - Math.abs(test.dot(worldUp)) < MIN_VALUE) {
      //( offset == worldUp || offset == -worldUp )
      // find the principal view direction other than top/bottom closest to
      // the current view direction and use it as an up vector

      var viewDir = this.getView().normalize();
      var optUpDir = [worldFront.clone(), worldFront.clone().negate(), worldRight.clone(), worldRight.clone().negate()];

      // use both view and up vectors for test vector because transitioning from
      // top and bottom views, view direction is the same (but up direction is different)

      var sign = test.dot(worldUp) > 0.0 ? +1.0 : -1.0; //( offset == worldUp ) ? +1.0 : -1.0;
      var testDir = viewDir.clone().add(this.camera.up.clone().multiplyScalar(sign)).normalize();

      var optValue = -2.0;

      for (var i = 0; i < 4; i++) {
        var value = testDir.dot(optUpDir[i]);

        if (value > optValue) {
          optValue = value;
          upDir = optUpDir[i].multiplyScalar(sign);
        }
      }
    }

    this._distFinal = this._distInitial = this.getView().length();
    // WHY? camParamsFinal.center = this.originalCenter;
    this._camParamsFinal.position.copy(this._camParamsFinal.center.clone().add(offset.multiplyScalar(this._distFinal / offset.length())));
    this._camParamsFinal.up.copy(upDir);

    var D = this._camParamsInitial.center.clone().sub(this._camParamsInitial.position).normalize();
    var R = D.clone().cross(this._camParamsInitial.up).normalize();
    var U = R.clone().cross(D).normalize();
    var M = new THREE.Matrix3();
    M.set(D.x, U.x, R.x, D.y, U.y, R.y, D.z, U.z, R.z);
    this._rotInitial.setFromRotationMatrix3(M);

    D = this._camParamsFinal.center.clone().sub(this._camParamsFinal.position).normalize();
    R = D.clone().cross(this._camParamsFinal.up).normalize();
    U = R.clone().cross(D).normalize();
    M.set(D.x, U.x, R.x, D.y, U.y, R.y, D.z, U.z, R.z);
    //TODO: figure out when these angles aren't supposed to be 0, works for now
    this._rotTwist.setFromAxisAngle(D, 0.0);
    this._rotSpin.setFromAxisAngle(U, 0.0);
    this._rotFinal.setFromRotationMatrix3(M);
    this._rotFinal.multiply(this._rotTwist).multiply(this._rotSpin).normalize();

  }

  /*         Functions for operation         */
  //convert screen coords to window coords
  _convertCoordsToWindow(pixelX, pixelY) {
    var delta = new THREE.Vector2(0, 0);

    var _window = this.getWindow();
    delta.x = pixelX / _window.innerWidth;
    delta.y = pixelY / _window.innerHeight;

    return delta;
  }

  _getNextRotation(rotationType, snapAngle, lastDelta) {
    var threshold, accelerationA, accelerationB, shiftZone;
    threshold = accelerationA = accelerationB = shiftZone = 0.0;

    var next = 0.0;
    var lockedAxis = null;
    var lockDelta = null;

    var deadZone = this.snapOrbitDeadZone;
    var orbitMultiplier = this.orbitMultiplier;

    if (rotationType == 'h') {
      threshold = this.snapOrbitThresholdH;
      accelerationA = this.snapOrbitAccelerationAX;
      accelerationB = this.snapOrbitAccelerationBX;
      shiftZone = 1.0 - this.snapOrbitAccelerationPointX;
      lockDelta = this.lockDeltaX;
      lockedAxis = this.lockedX;
    } else {
      threshold = this.snapOrbitThresholdV;
      accelerationA = this.snapOrbitAccelerationAY;
      accelerationB = this.snapOrbitAccelerationBY;
      shiftZone = 1.0 - this.snapOrbitAccelerationPointY;
      lockDelta = this.lockDeltaY;
      lockedAxis = this.lockedY;
    }

    if (!lockedAxis) {
      if (Math.abs(snapAngle) > threshold) {
        next = lastDelta * orbitMultiplier;
      } else if (Math.abs(snapAngle) > shiftZone * threshold) {
        if (lastDelta * snapAngle > 0.0) {
          next = lastDelta * orbitMultiplier * accelerationA;
        } else {
          next = lastDelta * orbitMultiplier * 1.0 / accelerationA;
        }

      } else {
        if (lastDelta * snapAngle > 0.0) {
          next = lastDelta * orbitMultiplier * accelerationB;
        } else {
          next = lastDelta * orbitMultiplier * 1.0 / accelerationB;
        }

      }

      if (next * snapAngle > 0.0 && Math.abs(next) > Math.abs(snapAngle)) {
        this.lockDeltaX = this.lockDeltaY = 0.0; //want to reset both regardless of rotation axis
        lockedAxis = true;
        next = snapAngle;
      }

    } else {
      lockDelta += lastDelta;

      if (lockDelta < -deadZone) {
        next = (lockDelta + deadZone) * orbitMultiplier * 1.0 / accelerationB;
        lockedAxis = false;
      } else if (lockDelta > deadZone) {
        next = (lockDelta - deadZone) * orbitMultiplier * 1.0 / accelerationB;
        lockedAxis = false;
      }
    }
    return next;
  }

  /// Returns true if the operation belongs to a chain of combined operations; otherwise returns false.
  _isCombined() {
    return this.combined;
  }

  _isInDeadZone(currentCursor, startCursor) {

    var deadZone = 30;
    var res = false;

    var _window = this.getWindow();
    var w = _window.innerWidth;
    var x = currentCursor.x % w;

    var h = _window.innerHeight;
    var y = currentCursor.y % h;


    var diffX = x > 0 ? x - startCursor.x : w + x - startCursor.x;
    var diffY = y > 0 ? y - startCursor.y : h + y - startCursor.y;

    if (Math.abs(diffX) < deadZone && Math.abs(diffY) < deadZone)
    res = true;

    return res;
  }

  /**
   * Draws a menu by appending an unordered list to the given container element.
   * @param {Array} menuOptions - string array of menu options, null meaning seperator
   * @param {Array} menuEnables - boolean array of menu enable flags indicating which corresponding menu entry in  menuOptions should be enabled or disabled.
   * @param {Number} mousex - the x coordinate of the menu trigger point, used to position menu
   * @param {Number} mousey - the y coordinate of the menu trigger point, used to position menu
   * @param {HTMLElement} container - the container element to add the menu to.
   * @param {Object} position - object with x, y, w, h of the container element.
   */
  drawDropdownMenu(menuOptions, menuEnables, menuCallbacks, mousex, mousey, container, position) {
    var itemID = 0;

    var _document = this.getDocument();
    if (!this._dropDownMenu) {

      this._dropDownMenu = _document.createElement('div');
      this._dropDownMenu.className = 'dropDownMenu';

      // Initialize the top and left with some approximate values
      // so that the correct width can be returned by getBoudningClientRect().
      this._dropDownMenu.style.top = '100px';
      this._dropDownMenu.style.left = '-400px';

      var menuHeight = 0;
      var menuMinWidth = 0;
      for (let i = 0; i < menuOptions.length; i++) {
        var listItem;
        if (menuOptions[i] === null) {// menu separator
          listItem = _document.createElement("li");
          listItem.style.height = '1px';
          menuHeight += 1;
          listItem.style.backgroundColor = "#E0E0E0";
        } else {
          var content = i18n.t(menuOptions[i]);
          menuMinWidth = content.length > menuMinWidth ? content.length : menuMinWidth;

          if (menuCallbacks[i]) {
            listItem = _document.createElement("div");
            var check = _document.createElement("input");
            var text = _document.createElement("label");
            check.type = "radio";
            check.className = "dropDownMenuCheck";
            text.textContent = content;
            text.className = "dropDownMenuCheckText";
            listItem.appendChild(check);
            listItem.appendChild(text);
            listItem.className = "dropDownMenuCheckbox";
          } else
          {
            listItem = _document.createElement("li");
            listItem.textContent = content;
            listItem.className = menuEnables[i] ? "dropDownMenuItem" : "dropDownMenuItemDisabled";
          }

          listItem.id = "menuItem" + itemID;
          itemID++;
          menuHeight += 25; // HACK!!!

          listItem.setAttribute("data-i18n", menuOptions[i]);
        }
        this._dropDownMenu.appendChild(listItem);
      }

      // Add the menu to the DOM before asking for boundingClientRect.
      // Otherwise, it will be zero.
      container.appendChild(this._dropDownMenu);

      this._dropDownMenu.style.minWidth = Math.max(256, menuMinWidth * 7.4) + 'px'; // approximate min width
      var menuWidth = this._dropDownMenu.getBoundingClientRect().width;

      this.menuSize.x = menuWidth;
      this.menuSize.y = menuHeight;
    } else
    {
      // Just add the drop down menu, It already exists.
      container.appendChild(this._dropDownMenu);
    }
    itemID = 0;
    for (let i = 0; i < menuOptions.length; i++) {
      if (menuOptions[i] === null)
      continue;

      if (menuCallbacks[i]) {
        var id = "menuItem" + itemID;
        var element = _document.getElementById(id);
        if (element) {
          element.children[0].checked = menuCallbacks[i]();
        }
      }
      itemID++;
    }
    var top = mousey - 15; // 15 offset so list appears @ button
    var left = mousex + 1;

    var rect = this.canvas.getBoundingClientRect();

    if (left + this.menuSize.x > rect.right)
    left = mousex - this.menuSize.x - 1;
    if (top + this.menuSize.y > rect.bottom)
    top = rect.bottom - this.menuSize.y;

    // Make relative to container:
    top -= position.y;
    left -= position.x;

    this._dropDownMenu.style.top = top + 'px';
    this._dropDownMenu.style.left = left + 'px';

    this.menuOrigin.x = left;
    this.menuOrigin.y = top;
  }

  removeDropdownMenu(container) {
    container.removeChild(this._dropDownMenu);
  }

  /**
   * Drops the cached dropdown menu so the next {@link drawDropdownMenu} call rebuilds it. Use after changes that
   * invalidate the cached DOM -- notably a locale switch, since menu labels are translated once at build time.
   */
  clearDropdownMenu() {
    if (!this._dropDownMenu) return;

    this._dropDownMenu.parentNode?.removeChild(this._dropDownMenu);
    this._dropDownMenu = null;
  }

  _isAxisAligned(vec) {
    var sceneRight = this.sceneFrontDirection.clone().cross(this.sceneUpDirection);
    var checkUp = Math.abs(Math.abs(vec.dot(this.sceneUpDirection)) - 1.0);
    var checkFront = Math.abs(Math.abs(vec.dot(this.sceneFrontDirection)) - 1.0);
    var checkRight = Math.abs(Math.abs(vec.dot(sceneRight)) - 1.0);

    return checkUp < EPSILON || checkFront < EPSILON || checkRight < EPSILON;
  }

  isFaceView() {
    var dir = this.center.clone().sub(this.camera.position).normalize();
    return this._isAxisAligned(dir) && this._isAxisAligned(this.camera.up);
  }

  startInteraction(x, y) {
    this.startCursor = new THREE.Vector2(x, y);

    this.startState = {
      saveCenter: this.center.clone(),
      saveEye: this.camera.position.clone(),
      savePivot: this.pivot.clone(),
      saveUp: this.camera.up.clone()
    };

    this.lockDeltaX = 0.0;
    this.lockedX = false;
    this.lastSnapRotateX = 0.0;
    this.lockDeltaY = 0.0;
    this.lockedY = false;
    this.lastSnapRotateY = 0.0;
    this.lastSnapDir = new THREE.Vector3(0, 0, 0);

    this.navApi.setTransitionActive(true);
  }

  orbit(currentCursor, startCursor, distance, startState) {
    if (!this.navApi.isActionEnabled('orbit') || this.currentlyAnimating === true)
    return;

    var mode = 'wheel';

    // If orthofaces is enabled, and camera is ortho
    // then switch to perspective
    if (this.orthographicFaces && !this.camera.isPerspective) {
      this.camera.toPerspective();

      // Hack: update the start state with the new position:
      if (startState)
      startState.saveEye.copy(this.camera.position);
    }
    if (startState) {
      mode = 'cube';
    }
    if (mode == 'cube') {
      this.saveCenter.copy(startState.saveCenter);
      this.saveEye.copy(startState.saveEye);
      this.savePivot.copy(startState.savePivot);
      this.saveUp.copy(startState.saveUp);
      this.useSnap = true;
      this.doCustomOrbit = true;
    } else {
      this.saveCenter.copy(this.center);
      this.savePivot.copy(this.pivot);
      this.saveEye.copy(this.camera.position);
      this.saveUp.copy(this.camera.up);
      this.useSnap = false;
      this.doCustomOrbit = false;
    }

    if (this._isCombined() && this._prevCenter == undefined) {
      this._prevCenter = this.saveCenter.clone();
      this._prevEye = this.saveEye.clone();
      this._prevPivot = this.savePivot.clone();
      this._prevUp = this.saveUp.clone();
    }

    // TODO: fold the two cases into one and prevent duplicate code
    if (this.preserveOrbitUpDirection) {

      var delta = this._convertCoordsToWindow(currentCursor.x - startCursor.x, currentCursor.y - startCursor.y);
      var lastDelta = this._convertCoordsToWindow(distance.x, distance.y);

      var worldUp = this.sceneUpDirection.clone();
      var worldFront = this.sceneFrontDirection.clone();
      var worldRight = this.sceneFrontDirection.clone().cross(this.sceneUpDirection).normalize();

      /* ????? WTF:
      var worldFront = new THREE.Vector3(1,0,0);
      var worldUp = new THREE.Vector3(0,1,0);
      */

      //viewcube
      // if (this.doCustomOrbit ) {
      //     worldUp = new THREE.Vector3(0,1,0);
      //     worldFront = new THREE.Vector3(1,0,0);
      // }

      /* ?????? WTF:
      var worldR = worldFront.clone().cross( worldUp );
      worldUp = worldR.clone().cross(worldFront);
      worldUp.clone().normalize();
      */

      var pivot = this._isCombined() ? this._prevPivot : this.savePivot;
      var eye = this._isCombined() ? this._prevEye : this.saveEye;
      var center = this._isCombined() ? this._prevCenter : this.saveCenter;
      var camUp = this._isCombined() ? this._prevUp : this.saveUp;

      var initViewDir = pivot.clone().sub(eye).normalize();
      var initViewDirV = center.clone().sub(eye).normalize();
      var initRightDir = initViewDirV.clone().cross(camUp);

      var fTargetDist = eye.clone().sub(pivot).length();
      var fTargetDistV = eye.clone().sub(center).length();

      var vLookUpdate = initViewDir.clone().multiplyScalar(-1);
      var vLookUpdateV = initViewDirV.clone().multiplyScalar(-1);
      var vRightUpdate = initRightDir;
      var vUpUpdate = camUp.clone();

      var snapAngleh = 0.0;
      var snapAnglev = 0.0;

      //viewcube

      if (!this.constrainOrbitHorizontal) {
        // Need to check if:
        //  1. camera is "upside-down" (angle between world up and camera up is obtuse) or
        //  2. camera is in top view (camera up perpendicular to world up and view angle acute to world up)
        // These cases required a reversed rotation direction to maintain consistent mapping of tool:
        //  left->clockwise, right->counter-clockwise
        //
        //  PHB June 2014 - #2 above makes no sense to me. If the camera up is perpendicular to the
        //  world up then the view is parallel to world up (view dot up == 1). So the second test is
        //  meaningless. There is no good way to determine the rotation direction in this case. If you
        //  want it to feel like direct manipulation then it would be better to determine if the cursor
        //  is above or below the pivot in screen space.

        var worldUpDotCamUp = worldUp.dot(this.saveUp);
        // var worldUpDotView  = worldUp.dot(this.saveCenter.clone().sub(this.saveEye).normalize());

        // if ((worldUpDotCamUp < -MIN_VALUE) ||
        //     ((Math.abs(worldUpDotCamUp) < MIN_VALUE) && (worldUpDotView > 0.0)))
        //
        var kFlipTolerance = 0.009; // Must be flipped by more than about 0.5 degrees
        if (worldUpDotCamUp < -kFlipTolerance) {
          delta.x = -delta.x;
          lastDelta.x = -lastDelta.x;
        }

        var dHorzAngle = 0.0;
        if (this._isCombined()) {
          dHorzAngle = lastDelta.x * this.orbitMultiplier;
        } else {
          dHorzAngle = this.useSnap ? this.lastSnapRotateX + this._getNextRotation('h', snapAngleh, -lastDelta.x) :
          delta.x * this.orbitMultiplier;
        }

        this.lastSnapRotateX = dHorzAngle;
        // Define rotation transformation

        var quatH = new THREE.Quaternion().setFromAxisAngle(worldUp, -dHorzAngle);

        vLookUpdate.applyQuaternion(quatH);
        vLookUpdateV.applyQuaternion(quatH);
        vRightUpdate.applyQuaternion(quatH);
        vUpUpdate.applyQuaternion(quatH);
      }

      if (!this.constrainOrbitVertical) {
        var vRightProjF = worldFront.clone().multiplyScalar(worldFront.dot(vRightUpdate));
        var vRightProjR = worldRight.clone().multiplyScalar(worldRight.dot(vRightUpdate));
        var vRightProj = vRightProjF.clone().add(vRightProjR);
        vRightProj.clone().normalize();

        var dVertAngle = 0.0;

        if (this._isCombined()) {
          dVertAngle = lastDelta.y * this.orbitMultiplier;
        } else {
          var next = this._getNextRotation('v', snapAnglev, lastDelta.y);
          dVertAngle = this.useSnap ? this.lastSnapRotateY + next : delta.y * this.orbitMultiplier;
        }
        var quatV = new THREE.Quaternion().setFromAxisAngle(vRightProj, -dVertAngle);

        if (!this.navApi.getOrbitPastWorldPoles()) {

          var vUpUpdateTemp = vUpUpdate.clone();
          vUpUpdateTemp.applyQuaternion(quatV).normalize();

          // Check if we've gone over the north or south poles:
          var wDotC = worldUp.dot(vUpUpdateTemp);
          if (wDotC < 0.0)
          {
            var vLookUpdateVtemp = vLookUpdateV.clone();
            vLookUpdateVtemp.applyQuaternion(quatV).normalize();

            // How far past Up are we?
            var dVertAngle2 = vLookUpdateVtemp.angleTo(worldUp);
            if (Math.abs(dVertAngle2) > Math.PI * 0.5)
            dVertAngle2 -= dVertAngle2 > 0.0 ? Math.PI : -Math.PI;

            dVertAngle -= dVertAngle2;

            quatV.setFromAxisAngle(vRightProj, -dVertAngle);
            vLookUpdate.applyQuaternion(quatV).normalize();
            vLookUpdateV.applyQuaternion(quatV).normalize();
            vUpUpdate.applyQuaternion(quatV).normalize();

          } else

          {
            vLookUpdate.applyQuaternion(quatV).normalize();
            vLookUpdateV.applyQuaternion(quatV).normalize();
            vUpUpdate.applyQuaternion(quatV).normalize();
          }
        } else

        {
          vLookUpdate.applyQuaternion(quatV).normalize();
          vLookUpdateV.applyQuaternion(quatV).normalize();
          vUpUpdate.applyQuaternion(quatV).normalize();
        }
        this.lastSnapRotateY = dVertAngle;
      }

      // figure out new eye point
      var vNewEye = vLookUpdate.multiplyScalar(fTargetDist).add(pivot);

      this.camera.position.copy(vNewEye);
      this.camera.up.copy(vUpUpdate);
      this.center.copy(vNewEye);
      this.center.sub(vLookUpdateV.multiplyScalar(fTargetDistV));

      if (this._isCombined())
      {
        this._prevCenter.copy(this.center);
        this._prevEye.copy(this.camera.position);
        this._prevPivot.copy(this.pivot);
        this._prevUp.copy(this.camera.up);
      }
    } else
    {












































      /*var lastDelta = convertCoordsToWindow(distance.x, distance.y);
      var vDir = prevPivot.clone().sub(prevEye);
      var vDirView = prevCenter.clone().sub(prevEye);
      var vRight = vDirView.clone().cross(prevUp);
      var vUp = vRight.clone().cross(vDirView);
      vUp.clone().normalize();
       var dist = (prevPivot.clone().sub(prevEye)).clone().length();
      var distView = (prevCenter.clone().sub(prevEye)).clone().length();
       var snapAngleh = 0.0;
      var snapAnglev = 0.0;
       //viewcube
      //snapToClosestView(vUp, snapAngleh, snapAnglev);
       if ( !this.constrainOrbitHorizontal ){
       var dHorzAngle = this.useSnap ? getNextRotation(HORIZONTAL, snapAngleh, lastDelta.x):
      lastDelta.x *this.orbitMultiplier;
       var quatH = new THREE.Quaternion().setFromAxisAngle( vUp.clone().normalize(), dHorzAngle );
      vDir = quatH.clone().rotate(vDir);
      vDirView = quatH.clone().rotate(vDirView);
      }
       if ( !this.constrainOrbitVertical ){
      var dVertAngle = this.useSnap ? getNextRotation(VERTICAL, snapAnglev, lastDelta.y) :
      lastDelta.y *this.orbitMultiplier;
       var quatV = new THREE.Quaternion().setFromAxisAngle( vRight.clone().normalize(), dVertAngle );
      vDir = quatV.clone().rotate(vDir);
      vDirView = quatV.clone().rotate(vDirView);
      vUp = quatV.clone().rotate(vUp);
      }
       camera.eye = this.pivot.clone().sub((vDir.clone().normalize()).clone().multiplyScalar(dist));
      this.center.copy(camera.eye.clone().add((vDirView.clone().normalize()).clone().multiplyScalar(distView)));
      camera.up.copy(vUp.clone().normalize());
       prevCenter = this.center;
      prevEye = camera.position;
      prevPivot = this.pivot;
      prevUp = camera.up;*/}this.camera.lookAt(this.center);this._changed(false); /*avp.logger.log("Camera Position: ( "+camera.position.x +", "+camera.position.y+", "+camera.position.z+" )");
    avp.logger.log("Up Vector: ( "+camera.up.x +", "+camera.up.y+", "+camera.up.z+" )");
    avp.logger.log("Center: ( "+this.center.x +", "+this.center.y+", "+this.center.z+" )");
    */}endInteraction() {this.navApi.setTransitionActive(false);}look(distance) {if (!this.navApi.isActionEnabled('walk')) return;var delta = this._convertCoordsToWindow(distance.x, distance.y);var multiplier = this.userLookSpeed; //if ( m_manager->GetApplicationParameters().lookInvertVerticalAxis ) { deltaY = -deltaY; }
    var eyeToCenter = this.getView();var camUp = this.camera.up;var camRight = eyeToCenter.clone().cross(camUp).normalize();var worldUp = this.sceneUpDirection.clone(); // TODO: scale look by camera's FOV
    // vertical rotation around the camera right vector
    var angle = delta.clone();angle.x *= Math.PI;angle.y *= Math.PI / this.camera.aspect;angle.multiplyScalar(multiplier);var qRotY = new THREE.Quaternion().setFromAxisAngle(camRight, -angle.y);if (this.camera.keepSceneUpright && !this.navApi.getOrbitPastWorldPoles()) {var futureUp = camUp.clone();
      futureUp.applyQuaternion(qRotY).normalize();

      if (futureUp.dot(worldUp) < 0) {
        var futureEyeToCenter = eyeToCenter.clone();
        futureEyeToCenter.applyQuaternion(qRotY);

        var deltaAngle = futureEyeToCenter.angleTo(worldUp);

        if (Math.abs(deltaAngle) > Math.PI * 0.5)
        deltaAngle -= deltaAngle > 0.0 ? Math.PI : -Math.PI;

        angle.y -= deltaAngle;

        qRotY.setFromAxisAngle(camRight, -angle.y);
      }
    }

    eyeToCenter = qRotY.clone().rotate(eyeToCenter);
    camUp = qRotY.clone().rotate(camUp);
    camUp.normalize();

    var vertAxis = this.camera.keepSceneUpright ? worldUp : camUp;
    var qRotX = new THREE.Quaternion().setFromAxisAngle(vertAxis, -angle.x);

    eyeToCenter = qRotX.clone().rotate(eyeToCenter);
    camUp = qRotX.clone().rotate(camUp);

    this.center.copy(eyeToCenter.add(this.camera.position));
    this.camera.up.copy(camUp);

    this.camera.lookAt(this.center);
    this._changed(false);
  }

}